/**
 * Navigation configuration parsing utilities
 */

import { Config } from "@backstage/config";
import {
  CatalogFilter,
  IconMappings,
  NavigationConfig,
  NavigationGroup,
  NavigationItem,
  NavigationSettings,
  StandaloneItem,
  UtilityItem,
} from "../types/navigation";

export function parseNavigationConfig(config: Config): NavigationConfig {
  const result: NavigationConfig = {};

  if (config.has("groups")) {
    result.groups = parseNavigationGroups(config.getConfigArray("groups"));
  }

  if (config.has("standalone")) {
    result.standalone = parseStandaloneItems(config.getConfigArray("standalone"));
  }

  if (config.has("utility")) {
    result.utility = parseUtilityItems(config.getConfigArray("utility"));
  }

  if (config.has("settings")) {
    result.settings = parseNavigationSettings(config.getConfig("settings"));
  }

  if (config.has("iconMappings")) {
    result.iconMappings = parseIconMappings(config.getConfig("iconMappings"));
  }

  return result;
}

function parseNavigationGroups(configs: Config[]): NavigationGroup[] {
  return configs.map((config) => ({
    id: config.getString("id"),
    title: config.getString("title"),
    icon: config.getOptionalString("icon"),
    priority: config.getNumber("priority"),
    items: config.getConfigArray("items").map(parseNavigationItem),
    allowDynamic: config.getOptionalBoolean("allowDynamic"),
  }));
}

function parseNavigationItem(config: Config): NavigationItem {
  const item: NavigationItem = {
    title: config.getString("title"),
  };

  if (config.has("filter")) {
    item.filter = parseFilter(config.getConfig("filter"));
  }

  if (config.has("to")) {
    item.to = config.getString("to");
  }

  if (config.has("icon")) {
    item.icon = config.getString("icon");
  }

  if (config.has("dynamic")) {
    item.dynamic = config.getBoolean("dynamic");
  }

  return item;
}

function parseFilter(config: Config): CatalogFilter {
  const filter: CatalogFilter = {};

  // Parse standard filter fields
  if (config.has("kind")) {
    const kind = config.get("kind");
    filter.kind = Array.isArray(kind)
      ? (kind as string[])
      : config.getString("kind");
  }

  if (config.has("type")) {
    const type = config.get("type");
    filter.type = Array.isArray(type)
      ? (type as string[])
      : config.getString("type");
  }

  if (config.has("tag")) {
    const tag = config.get("tag");
    filter.tag = Array.isArray(tag)
      ? (tag as string[])
      : [config.getString("tag")];
  }

  // Parse any additional filter fields (like metadata.namespace)
  config.keys().forEach((key) => {
    if (!["kind", "type", "tag"].includes(key)) {
      const value = config.get(key);
      filter[key] = value;
    }
  });

  return filter;
}

function parseNavigationSettings(config: Config): NavigationSettings {
  return {
    showOther: config.getOptionalBoolean("showOther"),
  };
}

function parseIconMappings(config: Config): IconMappings {
  const mappings: IconMappings = {};

  if (config.has("kinds")) {
    mappings.kinds = parseIconMap(config.getConfig("kinds"));
  }

  if (config.has("types")) {
    mappings.types = parseIconMap(config.getConfig("types"));
  }

  if (config.has("fallback")) {
    mappings.fallback = config.getString("fallback");
  }

  return mappings;
}

function parseIconMap(config: Config): Record<string, string> {
  const map: Record<string, string> = {};
  config.keys().forEach((key) => {
    map[key] = config.getString(key);
  });
  return map;
}

function parseStandaloneItems(configs: Config[]): StandaloneItem[] {
  return configs.map((config) => {
    const item: StandaloneItem = {
      id: config.getString("id"),
    };

    if (config.has("title")) {
      item.title = config.getString("title");
    }

    if (config.has("to")) {
      item.to = config.getString("to");
    }

    if (config.has("icon")) {
      item.icon = config.getString("icon");
    }

    if (config.has("priority")) {
      item.priority = config.getNumber("priority");
    }

    if (config.has("enabled")) {
      item.enabled = config.getBoolean("enabled");
    }

    return item;
  });
}

function parseUtilityItems(configs: Config[]): UtilityItem[] {
  return configs.map((config) => {
    const item: UtilityItem = {
      id: config.getString("id"),
    };

    if (config.has("title")) {
      item.title = config.getString("title");
    }

    if (config.has("to")) {
      item.to = config.getString("to");
    }

    if (config.has("icon")) {
      item.icon = config.getString("icon");
    }

    if (config.has("priority")) {
      item.priority = config.getNumber("priority");
    }

    if (config.has("enabled")) {
      item.enabled = config.getBoolean("enabled");
    }

    return item;
  });
}
